'use client';

import { Box, Typography } from '@mui/material';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInput } from '../formAdapters/FormInput/FormInput';
import { HelpdeskFileUpload } from './HelpdeskFileUpload/HelpdeskFileUpload';
import { QuestionTooltip } from '../QuestionTooltip/QuestionTooltip';
import { MAX_TEXT_LENGTH_2000 } from '../../constants';
import { AttachmentInfo } from '../../actions/helpdesk/types';
import { FormTextarea } from '../formAdapters/FormTextarea/FormTextarea';

export type HelpdeskFormTypes = {
  body: string;
  attachments: AttachmentInfo;
  contact_info: string;
};

type Props = {
  onSubmit: (values: HelpdeskFormTypes, methods: UseFormReturn<HelpdeskFormTypes>) => void;
  formControls: React.ReactNode;
};

export const HelpdeskForm = ({ onSubmit, formControls }: Props) => {
  const { t } = useTranslation();

  const methods = useForm<HelpdeskFormTypes>({
    defaultValues: {
      body: '',
      attachments: {
        filename: '',
        data: '',
        'mime-type': '',
      },
      contact_info: '',
    },
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit((values) => onSubmit(values, methods))}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: { xs: '100%', sm: 540 },
        }}
      >
        <Box>
          <Box mb={3}>
            <Typography variant="h5">{t('form.title')}</Typography>
          </Box>

          <FormTextarea
            name="body"
            label={t('form.problemLabel')}
            rules={{
              required: { value: true, message: t('errors.required') },
              validate: (val: string) =>
                (val.trim() === '' ? t('errors.required') : true) && val.length <= MAX_TEXT_LENGTH_2000,
            }}
            formControlMargin="none"
            minRows={10}
            maxRows={15}
            multilinePlaceholder={<>{t('form.problemPlaceholder')}</>}
            maxTextLength={MAX_TEXT_LENGTH_2000}
          />

          <HelpdeskFileUpload name="attachments.data" label={t('form.attachmentLabel')} />

          <FormInput
            name="contact_info"
            label={<QuestionTooltip label={t('form.contactLabel')} title={t('form.contactTooltip')} />}
            type="text"
            size="small"
            placeholder={t('form.contactPlaceholder')}
          />
        </Box>

        {formControls}
      </Box>
    </FormProvider>
  );
};
